/**
 * Task #611 — Artist Portal "Tour" widget.
 *
 * Shows the artist's committed tours and the next upcoming stop. Links
 * to /tour-day for the on-the-road view. Pure read-only — artists can't
 * reorder or commit; that all lives behind the admin/sales-rep
 * Master-Schedule Tour tab.
 */
import { useMemo } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, ExternalLink } from "lucide-react";

interface Tour {
  id: string;
  talentId: string;
  name: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  totalMiles: number | null;
  totalDriveMinutes: number | null;
}
interface TourStop {
  id: string;
  city: string;
  state: string;
  proposedDate: string | null;
}

interface ArtistTourWidgetProps {
  talentId: string;
}

export default function ArtistTourWidget({ talentId }: ArtistTourWidgetProps) {
  const toursQuery = useQuery<Tour[]>({
    queryKey: ["/api/tours/by-talent", talentId],
    enabled: !!talentId,
    queryFn: async () => {
      const res = await fetch(`/api/tours/by-talent/${talentId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load tours");
      return res.json();
    },
  });

  const tours = toursQuery.data ?? [];

  if (toursQuery.isLoading) return null;
  if (tours.length === 0) {
    return (
      <Card data-testid="card-artist-tour-widget">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="w-4 h-4" /> Tours
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground" data-testid="text-no-tours">
            No tours scheduled yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-artist-tour-widget">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="w-4 h-4" /> Tours
        </CardTitle>
        <Button asChild variant="outline" size="sm" data-testid="link-tour-day">
          <Link href="/tour-day">
            <ExternalLink className="w-3 h-3 mr-1" /> Tour Day View
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {tours.map((t) => (
            <div key={t.id} className="border rounded-md p-3" data-testid={`row-tour-${t.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium" data-testid={`text-tour-name-${t.id}`}>{t.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {t.startDate ? new Date(t.startDate).toLocaleDateString() : "TBD"}
                    {t.endDate ? ` – ${new Date(t.endDate).toLocaleDateString()}` : ""}
                  </div>
                </div>
                <Badge variant={t.status === "committed" ? "default" : "secondary"}>{t.status}</Badge>
              </div>
              {(t.totalMiles != null || t.totalDriveMinutes != null) && (
                <div className="text-xs text-muted-foreground mt-1">
                  {t.totalMiles ?? 0} mi · {Math.round((t.totalDriveMinutes ?? 0) / 60)}h drive
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
